import React from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  DragDropProvider,
  Grid,
  TableBandHeader,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  TableFixedColumns,
  TableGroupRow,
  TableHeaderRow,
  TableSelection,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import {
  GroupingState,
  IntegratedGrouping,
  IntegratedSelection,
  SelectionState,
  SortingState as GridSortingState,
} from '@devexpress/dx-react-grid';
import { TableViewComponentProps } from './types';
import { useFormatMessage } from '../../../../../locale';
import DataTypeProviders from '../../TypeProviders/DataTypeProviders';
import StickyTable from '../../../../lowLevel/StickyTable';
import TableHeaderTooltip from '../../../../lowLevel/TableHeaderTooltip';
import TableHeaderCell from '../TableHeaderCell';
import TableHeaderCellContent from '../TableHeaderCellContent';
import TableSelectCell from '../TableSelectCell/TableSelectCell';
import TableSelectAllCell from '../TableSelectAllCell';
import { TableSelectionPanelPlugin } from '../TableSelectionPanel';
import TableColumnVisibilityMessage from '../TableColumnVisibilityMessage';
import TableGroupSelectCell from '../TableGroupSelectCell';
import TableGroupCell from '../TableGroupCell';

const TableViewComponentVirtual = ({
  columns,
  columnBands,
  rows,
  tableProps,
  dataTypeProvidersProps,
  setSorting,
  sortingStateColumnExtensions,
  groupingStateColumnExtensions,
  someColumnHasTitle,
  someColumnSortable,
  columnWidths,
  setColumnWidths,
  columnOrder,
  setColumnOrder,
  enableColumnResizing,
  enableColumnReordering,
  columnVisibilityConfig,
  hiddenColumnNames,
  fixedColumns,
  rowSelectionConfig,
  tableSelectionStateRef,
  tableSelectionPanelRef,
  options: { sorting },
  groupingConfig,
}: TableViewComponentProps) => {
  const formatMessage = useFormatMessage();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <AutoSizer disableWidth>
      {({ height }) => (
        <Grid getRowId={(row) => row.id} rows={rows} columns={columns}>
          <GridSortingState
            sorting={sorting}
            columnExtensions={sortingStateColumnExtensions}
            onSortingChange={setSorting}
          />
          {rowSelectionConfig && <SelectionState />}
          {rowSelectionConfig && <IntegratedSelection />}
          {groupingConfig && (
            <GroupingState defaultGrouping={groupingConfig.defaultGrouping} />
          )}
          <DataTypeProviders {...dataTypeProvidersProps} />
          {enableColumnReordering && <DragDropProvider />}
          {groupingConfig && (
            <IntegratedGrouping
              columnExtensions={groupingStateColumnExtensions}
            />
          )}
          <VirtualTable
            {...tableProps}
            messages={{
              noData: formatMessage('noData'),
            }}
            estimatedRowHeight={60}
            height={height}
            tableComponent={StickyTable}
          />
          {enableColumnReordering && (
            <TableColumnReordering
              order={columnOrder}
              onOrderChange={setColumnOrder}
            />
          )}
          {enableColumnResizing && (
            <TableColumnResizing
              columnWidths={columnWidths}
              onColumnWidthsChange={setColumnWidths}
            />
          )}
          {someColumnHasTitle && (
            <TableHeaderRow
              sortLabelComponent={TableHeaderTooltip}
              showSortingControls={someColumnSortable}
              cellComponent={TableHeaderCell}
              contentComponent={TableHeaderCellContent}
            />
          )}
          {rowSelectionConfig && (
            <TableSelection
              showSelectAll
              cellComponent={TableSelectCell}
              headerCellComponent={TableSelectAllCell}
            />
          )}
          {rowSelectionConfig && (
            <TableSelectionPanelPlugin
              ref={tableSelectionStateRef}
              rowSelectionConfig={rowSelectionConfig}
              tableSelectionPanelRef={tableSelectionPanelRef}
            />
          )}
          {columnVisibilityConfig?.enabled && (
            <TableColumnVisibility
              hiddenColumnNames={hiddenColumnNames}
              messages={{
                noColumns: formatMessage('allColumnsAreHidden'),
              }}
              emptyMessageComponent={TableColumnVisibilityMessage}
            />
          )}
          {groupingConfig && (
            <TableGroupRow
              cellComponent={
                rowSelectionConfig ? TableGroupSelectCell : TableGroupCell
              }
            />
          )}
          {columnBands && <TableBandHeader columnBands={columnBands} />}
          {isMdUp && fixedColumns && <TableFixedColumns {...fixedColumns} />}
        </Grid>
      )}
    </AutoSizer>
  );
};

export default TableViewComponentVirtual;
